import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  PDFPrefillRequestDto,
  SignedDocumentDto,
  SignedDocumentsFindAllRequestDto,
  SignedDocumentsFindAllResponseDto,
  SignedDocumentsGetPresignedUrlsResponseDto,
  SignedDocumentsMarkVsaSignedRequestDto,
  SignedDocumentsSignRequestDto
} from './vive-api-models';

class SignedDocumentsService extends ViveApiBaseService {
  public async findAll(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<SignedDocumentsFindAllRequestDto, 'includeAssociations'>;
  }): Promise<SignedDocumentsFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<SignedDocumentsFindAllResponseDto>({
      apiEndpoint: 'signed-documents',
      options,
      queryParams
    });
  }

  public async findAllByVendor(params: {
    vendorId: number;
    options?: ViveApiRequestOptions;
    queryParams?: Omit<
      SignedDocumentsFindAllRequestDto,
      'VendorId' | 'includeAssociations'
    >;
  }): Promise<SignedDocumentsFindAllResponseDto> {
    const { vendorId, options, queryParams } = params;
    return await this.sendGet<SignedDocumentsFindAllResponseDto>({
      apiEndpoint: 'vendors/:vendorId/signed-documents',
      pathParams: { vendorId },
      options,
      queryParams
    });
  }

  public async getPresignedUrls(params: {
    vendorId: number;
    signedDocumentId: number;
    options?: ViveApiRequestOptions;
  }): Promise<SignedDocumentsGetPresignedUrlsResponseDto[]> {
    const { vendorId, signedDocumentId, options } = params;
    return await this.sendGet<SignedDocumentsGetPresignedUrlsResponseDto[]>({
      apiEndpoint:
        'vendors/:vendorId/signed-documents/:signedDocumentId/presigned-urls',
      pathParams: { vendorId, signedDocumentId },
      options
    });
  }

  public async prefill(params: {
    esignTemplateId: number;
    data: PDFPrefillRequestDto;
    options?: ViveApiRequestOptions;
  }): Promise<void> {
    const { esignTemplateId, data, options } = params;
    return await this.sendPost<void>({
      apiEndpoint: 'signed-documents/esign-templates/:esignTemplateId/prefill',
      pathParams: { esignTemplateId },
      data,
      options
    });
  }

  public async sign(params: {
    esignTemplateId: number;
    data: SignedDocumentsSignRequestDto;
    options?: ViveApiRequestOptions;
  }): Promise<SignedDocumentDto> {
    const { esignTemplateId, data, options } = params;
    return await this.sendPost<SignedDocumentDto>({
      apiEndpoint: 'signed-documents/esign-templates/:esignTemplateId/sign',
      pathParams: { esignTemplateId },
      data,
      options
    });
  }

  public async markVsaAsSigned(params: {
    companyId: number;
    vendorId: number;
    data: SignedDocumentsMarkVsaSignedRequestDto;
    options?: ViveApiRequestOptions;
  }): Promise<SignedDocumentDto> {
    const { companyId, vendorId, data, options } = params;
    return await this.sendPut<SignedDocumentDto>({
      apiEndpoint:
        'companies/:companyId/vendors/:vendorId/signed-documents/mark-vsa-signed',
      pathParams: { companyId, vendorId },
      data,
      options
    });
  }
}

export const signedDocumentsService = new SignedDocumentsService();
